import React, { useCallback, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  Switch
} from "@chakra-ui/react";
import PostEditorView from "./PostEditorView";
import * as PostEditorModes from "../PostEditor/PostEditorModes.constants";
import { translate as trans } from "../Trans/Trans";
import { useSelector } from "react-redux";

const PostEditorViewModal = ({ title, onClose, defaultAccountsIds, ...rest }) => {
  const { t } = useTranslation(["planner"]);

  const mediaSidebarPanelOpen = useSelector(
    (state) => state.plannerStore.mediaSidebarPanelOpen
  );
  const isActionPanelOpen = useSelector(
    (state) => state.rightsidebar.isActionPanelOpen
  );

  const socialAccounts = useSelector(
    (state) => state.socialAccountsStore.accounts
  );

  const [accountsIds, setAccountsIds] = useState(
    defaultAccountsIds || rest.defaultPostData?.accounts || []
  );

  const handleAccountsToggle = useCallback((event) => {
    if (event.target.checked) {
      const allAccountIds = socialAccounts.map((account) => account.id);

      setAccountsIds(allAccountIds);

    } else {
      setAccountsIds([]);
    }
  }, [socialAccounts]);

  const singleMode = useMemo(
    () =>
      [
        PostEditorModes.EDIT,
        PostEditorModes.CUSTOM_EDIT,
        PostEditorModes.DRAFT_EDIT,
      ].includes(rest.mode),
    [rest.mode]
  );

  useEffect(() => {
    if (document.activeElement) {
      document.activeElement.blur();
    }
  }, []);

  return (
    <Modal
      autoFocus={false}
      returnFocusOnClose={false}
      initialFocusRef={() => null}
      closeOnOverlayClick={false}
      isOpen
      onClose={onClose}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent
        transition="margin-left 250ms ease-in-out, margin-right 250ms ease-in-out"
        marginLeft={mediaSidebarPanelOpen ? "360px" : null}
        marginRight={isActionPanelOpen ? "370px" : null}
      >
        {rest.mode ? (
          <ModalHeader pl="4">
            <Flex alignItems="center">
            <Text mr="4">{trans(`post_editor_view.modal.${rest.mode}`)}</Text>
            {!singleMode && (
            <Flex alignItems="center">
              <Tooltip
                label={t("accounts.selected_tip")}
                placement="top"
                hasArrow
              >
                <Text
                  title={t("accounts.selected")}
                  fontSize="xs"
                  fontWeight="300"
                  color="gray.100"
                  mr="2"
                >
                  {accountsIds.length}/{socialAccounts.length}
                </Text>
              </Tooltip>
              <Switch
                onChange={handleAccountsToggle}
                isChecked={
                  accountsIds.length === socialAccounts.length
                }
              />
            </Flex>
            )}
            </Flex>
          </ModalHeader>
        ) : null}
        <ModalBody paddingX="0">
          <PostEditorView
            onChangeAccount
            defaultAccountsIds={accountsIds}
            onAccountsIdsChange={setAccountsIds}
            {...rest}
          />
        </ModalBody>
        <ModalCloseButton />
      </ModalContent>
    </Modal>
  );
};

PostEditorViewModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

PostEditorViewModal.defaultProps = {
  title: undefined,
};

export default PostEditorViewModal;
